// De tijdlijn van één persoon: dagkoppen, capaciteitsrij en de urgentie-lanes.
//
// Dichtheid is hier de hoofdzaak. Het oude scherm besteedde ~90px aan dagkoppen, ~55px
// aan de capaciteitsrij en per groep nog een rij die alleen "2 taken" zei. Op een
// gedeeld scherm ging daarmee de helft van het beeld op aan chrome. Hier staan de
// dagkop en de capaciteit elk op één regel en verhuist het aantal naar de lane-kop.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
} from '@mui/icons-material';

import { computeDayLoad, pctOfDay } from '../../utils/capacityCalc';
import { DAGSTAAT_STIJL, getCapacityBgColor, getCapacityColor } from '../../utils/capacityColors';
import {
  LANES, PRIO_LANES_INSTELBAAR, PRIO_LANE_KLEUR, PRIO_STANDAARD_INGEKLAPT, STANDAARD_INGEKLAPT,
  verdeelInLanes, verdeelInPrioLanes, zichtbareTaken,
} from '../../utils/dagstartLanes';
import { parseISODate } from '../../utils/weekUtils';
import TaskBlock, { AFSPRAAK_KLEUREN } from './TaskBlock';
import { maatVoor } from './dichtheden';
import { TOETS_STAP, useTimelineDrag, useToetsVerzet } from './useTimelineDrag';

/**
 * Lanes waarin je een blok mag verslepen of resizen.
 *
 * AFSPRAKEN niet: die komen uit Outlook en daar is Outlook de baas — een vergadering
 * verzetten doe je in de agenda, niet hier. ONGEPLAND niet: die taken hebben geen datums
 * en staan daarom buiten het dagenraster.
 */
// Slepen/verlengen kan overal behalve bij afspraken (uit Outlook) en ongeplande
// taken (geen datum om te verschuiven). Als blacklist, zodat de prioriteits-lanes
// ('Prio 1'…'Nog niet van toepassing') het ook doen — met de oude whitelist van
// urgentie-lanes was slepen in de prioriteitsweergave stilletjes uit (Joris, 1 sep).
const NIET_SLEEPBARE_LANES = new Set([LANES.AFSPRAKEN, LANES.ONGEPLAND, LANES.DRAAIDAGEN]);
const RASTER_LANES = new Set([LANES.ACHTERSTALLIG, LANES.AFSPRAKEN, LANES.ONGEPLAND]);  // draaidag-datum komt uit Outlook

const LABEL_BREEDTE = 104;
/** Onder deze breedte is een dagkolom onleesbaar; dan schuift de tijdlijn liever. */
const MIN_KOLOM = 88;

const dagLabel = (iso) => {
  const d = parseISODate(iso);
  if (!d) return iso;
  // "ma 10" — dag en datum op één regel; twee regels kostte 90px voor twee tekens.
  return `${d.toLocaleDateString('nl-NL', { weekday: 'short' }).replace('.', '')} ${d.getDate()}`;
};

/**
 * Taken over rijen verdelen zodat blokken die elkaar in de tijd overlappen niet op
 * elkaar landen. Simpele first-fit: elke rij houdt bij tot welke kolom hij bezet is.
 */
function pakInRijen(items) {
  const rijen = [];
  const uit = [];
  items.forEach((item) => {
    let rij = rijen.findIndex((tot) => tot < item.vanKolom);
    if (rij === -1) { rijen.push(-1); rij = rijen.length - 1; }
    rijen[rij] = item.totKolom;
    uit.push({ ...item, rij });
  });
  return { geplaatst: uit, aantalRijen: Math.max(1, rijen.length) };
}

/**
 * Waar staat dit blok in het zichtbare venster? Buiten beeld wordt afgeknipt.
 *
 * `buiten` markeert de twee gevallen waarin de getekende kolom níet de echte datum is:
 * de taak ligt volledig links of volledig rechts van het venster en staat vastgepind op
 * de rand. Wie zo'n blok versleept, verwacht dat het op de kolom landt waar hij loslaat —
 * niet dat de datum een dag opschuift vanaf een plek die je niet ziet. Zie het `anker` in
 * `datumsNaGebaar`. Een blok dat alleen links is afgeknipt maar met zijn eind wél in beeld
 * staat, krijgt géén `buiten`: die tekent zijn duur eerlijk en moet vanaf zijn echte
 * datums rekenen, anders raakt hij bij elke sleep stilletjes dagen kwijt.
 */
function kolommenVoor(taak, dagen) {
  const start = String(taak.startDate || '').slice(0, 10);
  const eind = String(taak.endDate || '').slice(0, 10) || start;
  const laatste = eind < start ? start : eind;
  if (!start) return null;

  // Werkdagen buiten het venster bestaan niet als kolom; klem naar de dichtstbijzijnde
  // rand zodat een blok buiten beeld toch zichtbaar blijft.
  //
  // findIndex faalt uitsluitend als de taak ná de laatste kolom begint — ligt de start
  // ervóór, dan is dagen[0] al >= start en krijg je gewoon 0. Die tak alsnog naar de
  // linkerrand klemmen was dus fout: een taak die pas in september gepland staat tekende
  // zich als balk over de vólle breedte van deze week, alsof hij er nu al lag.
  const van = dagen.findIndex((d) => d >= start);
  if (van === -1) {
    if (taak.type === 'Afspraak') return null;  // afspraken alleen op hun eigen dag
    return {
      vanKolom: dagen.length - 1, totKolom: dagen.length - 1,
      afgeknipt: 'rechts', buiten: 'rechts',
    };
  }

  let tot = -1;
  for (let i = dagen.length - 1; i >= 0; i -= 1) {
    if (dagen[i] <= laatste) { tot = i; break; }
  }
  // Geen enkele kolom valt op of vóór de einddatum: de taak ligt volledig links van het
  // venster. Wél tonen — een achterstallige taak verbergen is wat dit scherm voorkomt.
  if (tot === -1) return taak.type === 'Afspraak' ? null : { vanKolom: 0, totKolom: 0, afgeknipt: 'links', buiten: 'links' };
  // tot < van betekent dat start én eind in hetzelfde weekend vallen (weekenden zijn geen
  // kolom). Een taak dan op de eerstvolgende werkdag in plaats van hem te laten vallen —
  // een afspraak niet: die hoort op zijn eigen dag, en zaterdag is geen maandag (20 okt).
  if (tot < van) return taak.type === 'Afspraak' ? null : { vanKolom: van, totKolom: van, afgeknipt: null };

  return { vanKolom: van, totKolom: tot, afgeknipt: start < dagen[0] ? 'links' : null };
}

/**
 * Legenda bij de afsprakenlane. Kleur zonder uitleg is een raadsel op een gedeeld
 * scherm: zonder dit regeltje moet iedereen elk blok hoveren om te weten of dat paars
 * nou een klant, een collega of geblokte werktijd is.
 */
function AfspraakLegenda({ maat }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {[['extern', 'klant'], ['intern', 'intern'], ['taak', 'eigen tijd']].map(([soort, label]) => (
        <Box key={soort} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: 0.5, backgroundColor: AFSPRAAK_KLEUREN[soort] }} />
          <Typography sx={{ fontSize: maat.lane, color: maat.zachter }}>{label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function LaneKop({ lane, kleur, aantal, ingeklapt, onToggle, actie, maat }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.75,
        height: maat.blok < 40 ? 22 : 30, px: 0.75, mt: 0.5,
        borderTop: '1px solid #eef2f6',
      }}
    >
      {/* Het pijltje toont de stáát, niet de handeling: naar rechts = dicht, naar beneden
          = open. Andersom leest het als "wat gebeurt er als ik klik", en dan wijst hij
          precies verkeerd om. Kop én tekst zijn klikbaar — mikken op een icoon van 16px
          is onnodig priegelwerk. */}
      <Box
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        aria-expanded={!ingeklapt}
        aria-label={`${lane} in- of uitklappen`}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
          borderRadius: 1, px: 0.25, mx: -0.25,
          '&:hover': { backgroundColor: '#f8fafc' },
        }}
      >
        {ingeklapt ? <ChevronRightIcon sx={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#94a3b8' }} />}
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: kleur }} />
        <Typography sx={{ fontSize: maat.lane, fontWeight: 800, letterSpacing: '0.06em', color: maat.zacht }}>
          {lane} ({aantal})
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }} />
      {actie}
    </Box>
  );
}

function PersonTimeline({
  persoon,
  taken,
  horizonTaken = [],
  horizonDagen = [],
  dagen,
  agendaUren,
  regels,
  vandaagISO,
  dichtheid = 'comfortabel',
  onTaakKlik,
  onAfvinken,
  onNaarVandaag,
  onBulkNaarVandaag,
  onVerzet,
  onBezig,
  indeling = 'urgentie',
  onPrioWijzig,
  onNieuweTaak,
  verbergKlein = false,
  raster = null,
}) {
  // `raster` (Dagstart per persoon, Joris 4 nov): de lanes staan in dezelfde kolommen als de
  // roosterrij erboven — { naamBreedte, sjabloon, kolomVan } met kolomVan[i] = kolom van
  // dagen[i] in dat sjabloon (weekenden zijn smalle kolommen ertussen). Dan geen eigen dagkop
  // en capaciteitsbalk: die staan al in het rooster.
  const kolomVan = raster ? raster.kolomVan : null;
  // In het raster (Dagstart per persoon) altijd compact: de roosterrij erboven is al de overzichtslaag.
  const blokDichtheid = raster ? 'compact' : dichtheid;
  const maat = maatVoor(blokDichtheid);
  // Kijk je vooruit (venster begint ná vandaag), dan valt vrijwel alles in de
  // LATER-lane — die dan standaard dichtklappen maakt het scherm "leeg"
  // (taken onvindbaar in de urgentieweergave, Joris 1 sep). Dus: LATER alleen
  // dicht wanneer het venster bij vandaag begint.
  const kijktVooruit = (dagen?.[0] || '') > vandaagISO;
  const standaardIngeklapt = useCallback(() => {
    const standaard = indeling === 'prioriteit'
      ? PRIO_STANDAARD_INGEKLAPT
      : STANDAARD_INGEKLAPT.filter((l) => !(kijktVooruit && l === LANES.LATER));
    return Object.fromEntries(standaard.map((l) => [l, true]));
  }, [indeling, kijktVooruit]);
  const [ingeklapt, setIngeklapt] = useState(standaardIngeklapt);
  // Wisselen van indeling of venster betekent andere lanes; de dichtgeklapte
  // set hoort mee te gaan.
  useEffect(() => { setIngeklapt(standaardIngeklapt()); }, [standaardIngeklapt]);

  const uitgesloten = useMemo(() => new Set(regels?.excludedStatuses || []), [regels]);

  // Prioriteit aanpassen zonder de taak te openen: rechtsklik (of lang indrukken)
  // op een blok. Uit de feedback bij de prioriteitsweergave — daar wil je snel
  // kunnen schuiven zonder telkens het detailpaneel in te duiken.
  const [prioMenu, setPrioMenu] = useState(null);   // { x, y, taak }
  const opentPrioMenu = useCallback((event, taak) => {
    event.preventDefault();
    event.stopPropagation();
    setPrioMenu({ x: event.clientX, y: event.clientY, taak });
  }, []);

  // Het dagenraster is overal even breed (dezelfde gridTemplateColumns), dus één meting
  // op de dagkoppen volstaat om te weten hoeveel pixels één werkdag is.
  const rasterRef = useRef(null);
  const kolomBreedte = useCallback(() => {
    const el = rasterRef.current;
    if (!el || !dagen.length) return 0;
    // In een raster met weekendkolommen: meet één werkdagkolom.
    if (raster) return el.children[1]?.getBoundingClientRect().width || 0;
    return (el.getBoundingClientRect().width - LABEL_BREEDTE) / dagen.length;
  }, [dagen.length, raster]);

  // Raster met weekendkolommen: werkdagen liggen niet even ver uit elkaar. Reken een sleep
  // daarom af op de echte kolomposities (gemeten op de verborgen dagkop), vanaf de kolom
  // waar het gebaar begint; voorbij de rand met de gemiddelde afstand.
  const maakNaarDelta = useCallback((vanaf) => {
    const el = rasterRef.current;
    if (!raster || !el) return undefined;
    const midden = [...el.children].slice(1).map((c) => {
      const r = c.getBoundingClientRect();
      return r.left + r.width / 2;
    });
    if (midden.length < 2) return undefined;
    const stap = (midden[midden.length - 1] - midden[0]) / (midden.length - 1);
    return (dx) => {
      const doel = midden[vanaf] + dx;
      if (doel < midden[0]) return Math.round((doel - midden[0]) / stap) - vanaf;
      const laatste = midden.length - 1;
      if (doel > midden[laatste]) return laatste + Math.round((doel - midden[laatste]) / stap) - vanaf;
      let beste = 0;
      midden.forEach((x, i) => { if (Math.abs(x - doel) < Math.abs(midden[beste] - doel)) beste = i; });
      return beste - vanaf;
    };
  }, [raster]);

  const { gebaar, startGebaar } = useTimelineDrag({ onKlaar: onVerzet });
  const { toetsStand, verschuif, afbreken } = useToetsVerzet({ onKlaar: onVerzet });

  /**
   * Toetsen op een blok. ←/→ verzet een werkdag, met Shift vijf; Alt pakt de rechterrand
   * (duur), Alt+Shift de linkerrand. Enter opent het paneel, T zet naar vandaag.
   */
  const opToets = useCallback((e, taak, sleepbaar, anker) => {
    if (e.key === 'Enter') { e.preventDefault(); onTaakKlik(taak); return; }
    if (e.key === 'Escape') { if (afbreken()) e.preventDefault(); return; }
    if ((e.key === 't' || e.key === 'T') && onNaarVandaag) {
      e.preventDefault(); onNaarVandaag(taak); return;
    }
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    // Niet-sleepbare blokken (afspraken, ongepland) laten de pijltjes door naar de
    // browser, zodat scrollen met het toetsenbord blijft werken.
    if (!sleepbaar) return;
    e.preventDefault();
    const richting = e.key === 'ArrowRight' ? 1 : -1;
    const soort = e.altKey ? (e.shiftKey ? 'links' : 'rechts') : 'verplaats';
    // Shift betekent "de grotere variant": zonder Alt vijf dagen, met Alt de andere rand.
    const stap = (!e.altKey && e.shiftKey ? TOETS_STAP.groot : TOETS_STAP.klein) * richting;
    verschuif(taak, soort, stap);
  }, [onTaakKlik, onNaarVandaag, verschuif, afbreken]);

  // Muis en toetsenbord voeden dezelfde voorvertoning; ze kunnen niet tegelijk.
  const actieveStand = gebaar || toetsStand;

  // Het scherm erboven moet weten dat er een gebaar loopt, anders vervangt de
  // achtergrondronde de taken onder je cursor.
  useEffect(() => {
    if (!onBezig || !actieveStand) return undefined;
    onBezig(true);
    return () => onBezig(false);
  }, [onBezig, Boolean(actieveStand)]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wat er níet in beeld komt (vanallesuurtjes, werk dat pas ná het venster begint) —
  // zie `zichtbareTaken`. De capaciteit hieronder rekent bewust over álle taken door.
  const { zichtbaar, verborgen } = useMemo(
    () => zichtbareTaken(taken, vandaagISO, uitgesloten, {
      vensterEind: dagen[dagen.length - 1] || '', vensterStart: dagen[0] || '', verbergKlein,
    }),
    [taken, vandaagISO, uitgesloten, dagen, verbergKlein]
  );
  // Twee manieren om dezelfde week te lezen: op urgentie (wat is blijven liggen)
  // of op prioriteit (wat is het belangrijkst). De keuze staat in Dagstart.
  // Raster (Joris 4 nov, "dubbel"): de roosterrij toont draaidagen en taken al op hun dag. Hier
  // dan alleen wat het rooster niet laat zien: achterstallig, afspraken met tijden, ongepland.
  const lanes = useMemo(() => {
    if (raster) {
      return verdeelInLanes(zichtbaar, vandaagISO, uitgesloten).filter((l) => RASTER_LANES.has(l.lane));
    }
    return indeling === 'prioriteit'
      ? verdeelInPrioLanes(zichtbaar, vandaagISO, uitgesloten)
      : verdeelInLanes(zichtbaar, vandaagISO, uitgesloten);
  }, [zichtbaar, vandaagISO, uitgesloten, indeling, raster]);

  // De capaciteitsrij beweegt live mee tijdens het slepen — dat is dé vraag in een
  // dagstart ("past dat er nog bij?"). De lane-indeling doet dat bewust níet: die hangt
  // aan `taken` en bevriest dus vanzelf tijdens het gebaar. Zou een blok halverwege van
  // ACHTERSTALLIG naar VANDAAG springen, dan schiet het verticaal onder je cursor
  // vandaan terwijl het hele team meekijkt. Verhuizen gebeurt pas bij loslaten.
  const takenVoorCapaciteit = useMemo(() => {
    if (!actieveStand) return taken;
    return (taken || []).map((t) => (
      t.id === actieveStand.id
        ? { ...t, startDate: actieveStand.startDate, endDate: actieveStand.endDate }
        : t
    ));
  }, [taken, actieveStand]);

  // Dezelfde berekening als /capacity en Planner To-Do. Het oude Mijn Week vulde greedy
  // de minst bezette dag met een vaste 8 uur; dat gaf voor dezelfde persoon en week een
  // ander percentage dan het capaciteitsscherm.
  // De rij rekent door tot twee maanden voorbij het venster: taken die na de laatste zichtbare
  // dag doorlopen krijgen daar hun ruimte, in plaats van alles op de zichtbare dagen te
  // proppen (#62). Getoond worden alleen `dagen`; de horizon-dagen blijven onzichtbaar.
  const takenMetHorizon = useMemo(() => {
    const ids = new Set((takenVoorCapaciteit || []).map((t) => t.id));
    return [...(takenVoorCapaciteit || []), ...(horizonTaken || []).filter((t) => !ids.has(t.id))];
  }, [takenVoorCapaciteit, horizonTaken]);
  const dagenMetHorizon = useMemo(() => [...dagen, ...(horizonDagen || [])], [dagen, horizonDagen]);
  const { uren: urenPerDag, staat: dagStaat, extra: extraOpDraaidag, opbouw: opbouwPerDag } = useMemo(
    () => computeDayLoad(takenMetHorizon, dagenMetHorizon, agendaUren || {}, regels),
    [takenMetHorizon, dagenMetHorizon, agendaUren, regels]
  );

  const verborgenTekst = [
    verborgen.vanallesuurtje && `${verborgen.vanallesuurtje}× vanallesuurtje`,
    verborgen.buitenVenster && `${verborgen.buitenVenster} taak/taken die pas na ${dagen[dagen.length - 1]} starten`,
    verborgen.vrijeDag && `${verborgen.vrijeDag}× vrije dag die al voorbij is`,
    verborgen.achterstallig && `${verborgen.achterstallig} achterstallig (staat bij vandaag)`,
    verborgen.klein && `${verborgen.klein} kleine ${verborgen.klein === 1 ? 'taak' : 'taken'} (≤ 15 min)`,
  ].filter(Boolean).join(' · ');

  const kolommen = raster
    ? `${raster.naamBreedte}px ${raster.sjabloon}`
    : `${LABEL_BREEDTE}px repeat(${dagen.length}, minmax(0, 1fr))`;
  // Gridkolom (1-based, na de labelkolom) van werkdag i.
  const kolomNr = (i) => (kolomVan ? kolomVan[i] : i) + 2;
  const achterstallig = lanes.find((l) => l.lane === LANES.ACHTERSTALLIG);

  // Bij twintig kolommen wordt een dag ~65px breed en lees je geen enkele taaknaam meer;
  // dan schuift de tijdlijn liever horizontaal. Alléén dan, want `overflow-x: auto` maakt
  // van deze kaart een scrollcontainer en daarmee werkt de plakkende dagkop niet meer —
  // in de standaardweergave (5 of 10 dagen) is die kop belangrijker.
  const schuift = !raster && dagen.length * MIN_KOLOM + LABEL_BREEDTE > 1400;

  return (
    <Box sx={{
      backgroundColor: '#fff', borderRadius: 2, border: raster ? 'none' : '1px solid #e8edf2',
      // 'clip' i.p.v. 'hidden': hidden maakt van deze kaart een scroll-context
      // waardoor de plakkende dagkop nooit plakte (Joris, 1 sep); clip knipt
      // hetzelfde af maar laat position:sticky met de pagina meewerken.
      // Bij brede vensters scrollt de gedeelde container in Dagstart (Airtable-
      // stijl scrollbar onderaan) — deze kaart wordt dan alleen breder.
      // In een raster scrolt de container eromheen (Dagstart): hier niets afknippen.
      overflowX: raster ? 'visible' : 'clip',
      minWidth: schuift ? dagen.length * MIN_KOLOM + LABEL_BREEDTE : 0,
    }}>
      {persoon && (
        <Box sx={{ px: 1.25, py: 0.5, borderBottom: '1px solid #eef2f6', backgroundColor: '#fafbfc' }}>
          <Typography sx={{ fontFamily: '"Manrope", system-ui, sans-serif', fontSize: '0.85rem', fontWeight: 800, color: '#2d3335' }}>
            {persoon}
          </Typography>
        </Box>
      )}

      <Box sx={{ minWidth: schuift ? dagen.length * MIN_KOLOM + LABEL_BREEDTE : 0 }}>
      {/* Dagkoppen + capaciteit plakken: bij tien lanes wil je nog steeds weten welke
          dag welke kolom is en hoe vol die dag staat. */}
      <Box sx={raster
        // Onzichtbaar maar gemeten: de kolombreedte voor slepen komt van deze rij.
        ? { height: 0, overflow: 'hidden', visibility: 'hidden' }
        : { position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#fff' }}>
        <Box ref={rasterRef} sx={{ display: 'grid', gridTemplateColumns: kolommen, height: maat.blok < 40 ? 26 : 34, alignItems: 'center', borderBottom: '1px solid #eef2f6' }}>
          <Box />
          {dagen.map((d, i) => (
            <Box key={d} sx={{
              gridColumn: `${kolomNr(i)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              backgroundColor: d === vandaagISO ? '#f0f9ff' : 'transparent',
              borderRadius: 0.5, py: 0.25,
              '&:hover .dagPlus': { opacity: 1 },
            }}>
              <Typography sx={{ textAlign: 'center', fontSize: maat.dagkop, fontWeight: 700,
                color: d === vandaagISO ? '#0284c7' : maat.zacht }}>
                {dagLabel(d)}
              </Typography>
              {onNieuweTaak ? (
                <Box component="button" className="dagPlus" title="Taak toevoegen op deze dag"
                  onClick={() => onNieuweTaak(d)}
                  sx={{ border: 'none', background: 'none', cursor: 'pointer', p: 0, lineHeight: 1,
                    fontSize: maat.dagkop + 2, fontWeight: 800, color: '#0284c7',
                    opacity: 0.25, transition: 'opacity 0.15s' }}>
                  +
                </Box>
              ) : null}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: kolommen, height: maat.blok < 40 ? 28 : 38, alignItems: 'stretch', borderBottom: '1px solid #eef2f6' }}>
          <Typography sx={{ fontSize: maat.lane, fontWeight: 700, color: maat.zachter, pl: 1, alignSelf: 'center' }}>
            Capaciteit
          </Typography>
          {dagen.map((d) => {
            const uren = urenPerDag[d] || 0;
            const pct = pctOfDay(uren, regels?.hoursPerDay);
            // Een vrije dag en een draaidag zijn geen percentage maar een toestand: je
            // kunt er niets op plannen. Airtable toonde dat met een eigen kleur en het
            // woord "Vrij" — zonder dat leest een vrije dag als een lege dag met ruimte.
            const stijl = DAGSTAAT_STIJL[dagStaat[d]];
            const extra = extraOpDraaidag[d] || 0;
            const uitleg = stijl
              ? `${stijl.titel}${extra > 0 ? ` — let op: er staat ook nog ${extra.toFixed(1)} uur ander werk op deze dag` : ''}`
              : `${uren.toFixed(1)} uur van ${regels?.hoursPerDay || 8} = ${pct}%`;
            // De rekensom achter het getal, regel voor regel — zodat het percentage
            // naast Airtable te leggen is (Joris, 1 sep).
            const regelsVanDag = opbouwPerDag?.[d] || [];
            return (
              <Tooltip
                key={d}
                arrow
                title={(
                  <Box sx={{ fontSize: 11.5, lineHeight: 1.5 }}>
                    <b>{uitleg}</b>
                    {regelsVanDag.map((r, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 0.75, justifyContent: 'space-between' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{r.label}</span>
                        <span style={{ flexShrink: 0 }}>{r.uren.toFixed(1)}u</span>
                      </Box>
                    ))}
                    {verborgenTekst ? <Box sx={{ mt: 0.5, opacity: 0.8 }}>Inclusief wat hieronder niet in beeld staat ({verborgenTekst}).</Box> : null}
                  </Box>
                )}
              >
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  m: '2px', borderRadius: 0.5,
                  // `background`: een optie-draaidag heeft een gestreepte vulling.
                  background: stijl ? stijl.bg : getCapacityBgColor(pct),
                  borderBottom: `2px solid ${stijl ? stijl.rand : getCapacityColor(pct)}`,
                }}>
                  <Typography sx={{
                    fontSize: maat.capaciteit, fontWeight: 800,
                    color: stijl ? stijl.kleur : (pct > 0 ? '#334155' : '#cbd5e1'),
                  }}>
                    {stijl ? stijl.tekst : `${pct}%`}
                    {extra > 0 && ' ⚠'}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* In het raster staan geplande taken in de roosterrij erboven: lege lanes zijn daar geen "niets". */}
      {lanes.length === 0 && !raster && (
        <Typography sx={{ p: 2, fontSize: '0.8rem', color: '#94a3b8' }}>
          Geen taken in deze periode.
        </Typography>
      )}

      {lanes.map(({ lane, kleur, taken: laneTaken }) => {
        const dicht = ingeklapt[lane];
        const isOngepland = lane === LANES.ONGEPLAND;

        return (
          <Box key={lane}>
            <LaneKop
              lane={lane}
              kleur={kleur}
              maat={maat}
              aantal={laneTaken.length}
              ingeklapt={dicht}
              onToggle={() => setIngeklapt((p) => ({ ...p, [lane]: !p[lane] }))}
              actie={lane === LANES.ACHTERSTALLIG && onBulkNaarVandaag ? (
                <Button
                  size="small"
                  startIcon={<TodayIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onBulkNaarVandaag(achterstallig?.taken || [])}
                  sx={{ textTransform: 'none', fontSize: '0.66rem', py: 0, minHeight: 0, color: '#2563eb' }}
                >
                  alles naar vandaag
                </Button>
              ) : lane === LANES.AFSPRAKEN ? <AfspraakLegenda maat={maat} /> : null}
            />

            {!dicht && isOngepland && (
              // Ongeplande taken hebben geen datums en dus geen kolom. Ze staan als
              // losse blokken onder de tijdlijn in plaats van in een eigen kolom links:
              // dat zou de dagkolommen smaller maken voor iets wat je zelden ziet.
              <Box sx={{ display: 'grid', gridTemplateColumns: kolommen }}>
                <Box />
                <Box sx={{ gridColumn: `2 / -1`, display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.25 }}>
                  {laneTaken.map((t) => (
                    <Box key={t.id} sx={{ maxWidth: 260, minWidth: 160 }}>
                      <TaskBlock
                        taak={t}
                        lane={lane}
                        dichtheid={blokDichtheid}
                        vandaagISO={vandaagISO}
                        regels={regels}
                        onKlik={() => onTaakKlik(t)}
                        onAfvinken={onAfvinken}
                        onNaarVandaag={onNaarVandaag}
                        onToets={(event, taak) => opToets(event, taak, false)}
                        onPrioMenu={onPrioWijzig ? (e) => opentPrioMenu(e, t) : undefined}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {!dicht && !isOngepland && (() => {
              // First-fit werkt alleen als je van links naar rechts plaatst; de sortering
              // uit verdeelInLanes is op prioriteit en zou hier gaten geven.
              const met = laneTaken
                .map((t) => ({ taak: t, ...(kolommenVoor(t, dagen) || {}) }))
                .filter((x) => x.vanKolom !== undefined)
                // Draaidagen bovenaan: altijd de hoogste prio van de dag (feedback #54).
                .sort((a, b) => ((b.taak?.type === 'Draaidag') - (a.taak?.type === 'Draaidag')) || a.vanKolom - b.vanKolom || a.totKolom - b.totKolom);
              const { geplaatst, aantalRijen } = pakInRijen(met);
              const rijHoogte = maat.blok + (raster ? 2 : 4);

              return (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: kolommen,
                  gridTemplateRows: `repeat(${aantalRijen}, ${rijHoogte}px)`,
                  // Verticale daglijnen: zonder scheiding zie je bij vier weken niet
                  // meer waar het ene blok eindigt en het andere begint.
                  ...(raster ? {} : {
                    backgroundImage: `repeating-linear-gradient(to right, transparent, transparent calc(100% - 1px), #f1f5f9 calc(100% - 1px), #f1f5f9 100%)`,
                    backgroundSize: `calc((100% - ${LABEL_BREEDTE}px) / ${dagen.length}) 100%`,
                    backgroundPosition: `${LABEL_BREEDTE}px 0`,
                    backgroundRepeat: 'repeat',
                  }),
                }}>
                  {geplaatst.map(({ taak, vanKolom, totKolom, rij, afgeknipt, buiten }) => {
                    const sleepbaar = Boolean(onVerzet) && !NIET_SLEEPBARE_LANES.has(lane);
                    // Alleen vastgepinde blokken rekenen op kolommen; de rest op datums.
                    const anker = buiten ? { dagen, kolom: vanKolom } : null;
                    const stand = actieveStand?.id === taak.id ? actieveStand : null;
                    // Tijdens het gebaar schuiven alleen de kolommen van dít blok mee.
                    // Klemmen op het venster: sleep je een taak voorbij de rand, dan
                    // blijft hij op de laatste kolom staan terwijl de datum wél doorloopt
                    // — bij loslaten verdwijnt hij netjes uit beeld op zijn echte dag.
                    const van = stand && stand.soort !== 'rechts' ? vanKolom + stand.delta : vanKolom;
                    const tot = stand && stand.soort !== 'links' ? totKolom + stand.delta : totKolom;
                    const vanK = Math.min(Math.max(van, 0), dagen.length - 1);
                    const totK = Math.min(Math.max(tot, vanK), dagen.length - 1);

                    return (
                      <Box
                        key={taak.id}
                        sx={{
                          gridColumn: `${kolomNr(vanK)} / ${kolomNr(totK) + 1}`,
                          gridRow: rij + 1,
                          minWidth: 0,
                          // Het blok dat je vasthebt hoort bovenop te liggen, niet half
                          // onder het blok waar je overheen sleept.
                          zIndex: stand ? 2 : undefined,
                          // Een blok dat vóór het venster begon krijgt een pijl-rand, zodat
                          // niemand denkt dat het werk op maandag begon.
                          borderLeft: afgeknipt === 'links' && !stand ? '2px dotted #94a3b8' : undefined,
                        }}
                      >
                        <TaskBlock
                          taak={taak}
                          lane={lane}
                          dichtheid={blokDichtheid}
                          vandaagISO={vandaagISO}
                          regels={regels}
                          onKlik={() => onTaakKlik(taak)}
                          onAfvinken={onAfvinken}
                          onNaarVandaag={onNaarVandaag}
                          sleepbaar={sleepbaar}
                          sleepStand={stand}
                          onToets={(event, t) => opToets(event, t, sleepbaar, anker)}
                          onGebaar={(event, soort) => startGebaar({
                            event, taak, soort, anker,
                            kolomBreedte: kolomBreedte(),
                            naarDelta: maakNaarDelta(soort === 'rechts' ? totK : vanK),
                            onKlik: () => onTaakKlik(taak),
                          })}
                          onPrioMenu={onPrioWijzig ? (e) => opentPrioMenu(e, taak) : undefined}
                        />
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}
          </Box>
        );
      })}

      {/* Verbergen mag, stil verbergen niet: de capaciteit hierboven telt deze taken
          gewoon mee, dus moet je kunnen zien waaróm het cijfer hoger is dan de blokken. */}
      {verborgenTekst && (
        <Typography sx={{ px: 1, py: 0.5, fontSize: '0.64rem', color: '#94a3b8', borderTop: '1px solid #f1f5f9' }}>
          Niet in beeld: {verborgenTekst} — tellen wel mee in de capaciteit.
        </Typography>
      )}
      </Box>

      <Menu
        open={Boolean(prioMenu)}
        onClose={() => setPrioMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={prioMenu ? { top: prioMenu.y, left: prioMenu.x } : undefined}
      >
        <MenuItem disabled sx={{ fontSize: '0.72rem', opacity: 0.8 }}>
          {prioMenu?.taak?.name?.slice(0, 40) || 'Taak'}
        </MenuItem>
        {PRIO_LANES_INSTELBAAR.map((p) => (
          <MenuItem
            key={p}
            selected={String(prioMenu?.taak?.prio || '') === p}
            onClick={() => { onPrioWijzig?.(prioMenu.taak, p); setPrioMenu(null); }}
            sx={{ fontSize: '0.8rem' }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', mr: 1, bgcolor: PRIO_LANE_KLEUR[p] || '#94a3b8' }} />
            {p}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

// Gememoiseerd: bij "Alle" staan er ±34 tijdlijnen met samen ±1200 blokken. Zonder memo
// tekende elke state-wijziging in Dagstart (paneel openen, typen, één verzette taak) ze
// allemaal opnieuw, inclusief capaciteit over de horizon van twee maanden.
export default React.memo(PersonTimeline);
